import {
  DebouncedLoader,
  ErrorsContextProvider,
  RHFormCheckbox,
  RHFormSelect,
  ValidationErrors,
} from 'components';
import { Message, Form, Modal, Input, Segment, Grid, Header } from 'semantic-ui-react';
import { FormProvider, useWatch } from 'react-hook-form';
import { BaseSyntheticEvent, useDeferredValue, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';

import { useAddRoomToLrwSession } from './useAddRoomToLrwSession';
import { AvailableRoomsTable } from './AvailableRoomsTable';
import { Footer } from './Footer';
import { Label } from '@britishcouncil/react-admin';

export const AddRoomToLrwSession = () => {
  const {
    sessionId,
    isFetching,
    availableRooms,
    agents,
    isSaving,
    isSaved,
    handleSubmit,
    form,
    generalErrors,
    isSessionReleased,
    isClosed,
    agentId,
  } = useAddRoomToLrwSession();

  const [searchInputValue, setSearchInputValue] = useState('');
  const deferredSearchTerm = useDeferredValue(searchInputValue);

  const agentOptions = useMemo(
    () =>
      agents?.map((agent) => ({
        key: agent.id,
        value: agent.id,
        text: agent.name ?? '',
      })) ?? [],
    [agents]
  );

  const hasAgentSelected = typeof agentId === 'number' && Number.isFinite(agentId) && agentId > 0;

  const isPublic = useWatch({ control: form.control, name: 'sessionTypeFilter.public' });
  const isClosedFilter = useWatch({ control: form.control, name: 'sessionTypeFilter.closed' });

  useEffect(() => {
    if (isPublic) {
      form.setValue('sessionTypeFilter.closed', false, { shouldDirty: true });
      form.setValue('agentId', null, { shouldDirty: true, shouldValidate: true });
    }
  }, [isPublic, form]);

  useEffect(() => {
    if (isClosedFilter) {
      form.setValue('sessionTypeFilter.public', false, { shouldDirty: true });
      form.setValue('agentId', null, { shouldDirty: true, shouldValidate: true });
    }
  }, [isClosedFilter, form]);

  return (
    <ErrorsContextProvider generalErrors={generalErrors}>
      <Modal
        open
        as={Form}
        onSubmit={(e: BaseSyntheticEvent) => form.handleSubmit(handleSubmit)(e)}
        data-testid="add-rooms-modal-lrw"
      >
        <FormProvider {...form}>
          <Modal.Header>Add room(s)</Modal.Header>

          <Modal.Content>
            {isSessionReleased && (
              <S.InfoMessage
                icon="info circle"
                color="blue"
                header="Session already released"
                content="Please be aware that rooms added here may instantly be available in sales channel."
              />
            )}

            <S.ValidationErrors />

            <S.FiltersSegment>
              <Grid columns={2}>
                <Grid.Column width={6}>
                  <Header as="h5">Session type</Header>

                  <S.SessionTypeRow className="inline">
                    <S.SessionTypeField>
                      <RHFormCheckbox
                        control={form.control}
                        name="sessionTypeFilter.public"
                        label=""
                        className="large fitted"
                      />
                      <Label color="blue" size="large">
                        Public
                      </Label>
                    </S.SessionTypeField>

                    <S.SessionTypeField>
                      <RHFormCheckbox
                        control={form.control}
                        name="sessionTypeFilter.closed"
                        label=""
                        className="large fitted"
                      />
                      <Label color="grey" size="large">
                        Closed
                      </Label>
                    </S.SessionTypeField>
                  </S.SessionTypeRow>
                </Grid.Column>

                <Grid.Column width={6}>
                  {isClosed && (
                    <>
                      <Header as="h5">Agent</Header>
                      <RHFormSelect
                        control={form.control}
                        name="agentId"
                        placeholder=""
                        fluid
                        selection
                        search
                        clearable
                        options={agentOptions}
                      />
                    </>
                  )}
                </Grid.Column>
              </Grid>
            </S.FiltersSegment>

            <S.TableContainer>
              {!availableRooms || isFetching ? (
                <DebouncedLoader margin="10rem 0" />
              ) : (
                <>
                  <S.TableHeaderBar>
                    <S.TableHeaderTitle>Available rooms</S.TableHeaderTitle>
                    <S.SearchInput
                      size="mini"
                      placeholder="Find room"
                      value={searchInputValue}
                      onChange={(_e, v) => setSearchInputValue(String(v.value ?? ''))}
                    />
                  </S.TableHeaderBar>

                  <AvailableRoomsTable
                    availableRooms={availableRooms}
                    searchTerm={deferredSearchTerm}
                  />
                </>
              )}
            </S.TableContainer>
          </Modal.Content>

          <Footer
            isSaving={isSaving}
            disabled={!form.formState.isValid || (isClosed && !hasAgentSelected)}
            saved={isSaved}
            sessionId={sessionId}
          />
        </FormProvider>
      </Modal>
    </ErrorsContextProvider>
  );
};

const S = {
  TableContainer: styled.div`
    margin: calc(-1rem - 1px) calc(-1.5rem + 1px);
    max-height: 50vh;
    overflow-y: auto;
    overflow-x: hidden;
  `,

  FiltersSegment: styled(Segment)`
    margin-bottom: 2.5rem !important;
  `,

  TableHeaderBar: styled.div`
    display: flex;
    align-items: center;
    background-color: #f9fafb;
    border: 1px solid rgba(34, 36, 38, 0.15);
    border-bottom: none;
  `,

  TableHeaderTitle: styled.div`
    font-weight: 600;
    background-color: #fff;
    border-bottom: none;
    padding: 0.75rem;
    text-align: left;
    margin-bottom: -1px;
    padding-right: 2rem;
  `,

  SearchInput: styled(Input)`
    &.ui.input {
      width: 250px;
      align-self: stretch;
      display: flex;
    }
    &.ui.input.focus > input {
      border-radius: 0;
    }
    &.ui.input > input {
      background-color: #fff;
      border-radius: 0;
      border: 0;
      border-left: 1px solid rgba(34, 36, 38, 0.15);
      border-right: 1px solid rgba(34, 36, 38, 0.15);
      height: 100%;
      font-size: 1rem;
    }
    &.ui.input > input:focus,
    &.ui.input > input:active {
      border-radius: 0;
    }
    &.ui.input > input::placeholder {
      font-size: 1rem;
    }
  `,

  ValidationErrors: styled(ValidationErrors)`
    &.ui.error.message {
      margin-bottom: 3rem;
    }
  `,

  SessionTypeRow: styled(Form.Group)`
    margin-top: 0.5rem;
    display: flex !important;
    gap: 2rem;
  `,

  SessionTypeField: styled(Form.Field)`
    display: inline-flex !important;
    align-items: center;
    gap: 0rem;

    .ui.checkbox {
      margin-right: -2rem !important;
    }
  `,

  InfoMessage: styled(Message)`
    &.ui.message {
      margin-bottom: 2rem !important;
    }
  `,
};
